// An entry's hover card in the Compendium (issue 1585, slice 3): the site's own rules hover card (its name, its family,
// the family's quick stats, or a one-sentence summary when it has none), read through this origin once per entry per
// page (`entryCard`), so a player's card is the public rules text and nothing of the table. Raised by the row it
// describes (`EntryRow`: the pointer resting on it, keyboard focus, a press-and-hold on a touch screen) and drawn as a
// NON-MODAL popover beside it, kept inside the viewport. It sits inside the row in the DOM, so Tab reaches its buttons
// from the row and the pointer may move onto it without it closing. Its buttons: Open, ★ Star / Unstar, and the DM's
// Place or Give to… where the row has them.
//
// qa-runs 407: the DM's Pin to DM screen (★ Pinned, with Unpin) where the row has it; and the DM screen's tile opens the
// same card on a click, with "Open on the site" and Close in place of the star.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { entryCard, type EntryCardAnswer } from '../../compendium';

export const CARD_WIDTH = 288;
const GAP = 6;
const MARGIN = 8;

export interface EntryCardProps {
  campaignId: string;
  segment: string;
  slug: string;
  /** The row's name and family, shown while the card is read. */
  name: string;
  family: string;
  /** The row the card describes: it is drawn beside it. */
  anchor: HTMLElement | null;
  starred?: boolean;
  /** The ★ is being changed now. */
  starring?: boolean;
  onOpen: () => void;
  /** The site favourite's toggle; absent, the card has no star (the DM screen's tile). */
  onStar?: (() => void) | undefined;
  /** The Open button's words ("Open on the site" on a tile). */
  openLabel?: string;
  /** The DM's pin, where the row has it: pinned now, being changed now, and the toggle. */
  pinned?: boolean;
  pinning?: boolean;
  onPin?: (() => void) | undefined;
  /** A Close button (a card opened by a click). */
  onClose?: (() => void) | undefined;
  /** The DM's, where the row has it. */
  onPlace?: (() => void) | undefined;
  onGive?: (() => void) | undefined;
}

/** Beside the anchor: to its right when there is room, else to its left; its top level with the row, inside the viewport. */
export function cardPlace(r: { left: number; right: number; top: number; bottom: number }, height: number, vw: number, vh: number): { left: number; top: number } {
  const right = r.right + GAP;
  const left = right + CARD_WIDTH + MARGIN <= vw ? right : Math.max(MARGIN, r.left - GAP - CARD_WIDTH);
  const top = Math.max(MARGIN, Math.min(r.top, vh - height - MARGIN));
  return { left: Math.min(left, Math.max(MARGIN, vw - CARD_WIDTH - MARGIN)), top };
}

const btn = 'rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800 disabled:opacity-50';

export function EntryCard({ campaignId, segment, slug, name, family, anchor, starred = false, starring = false, onOpen, onStar, openLabel = 'Open', pinned = false, pinning = false, onPin, onClose, onPlace, onGive }: EntryCardProps) {
  const [got, setGot] = useState<EntryCardAnswer | 'loading'>('loading');
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const self = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let live = true;
    setGot('loading');
    Promise.resolve().then(() => entryCard(campaignId, segment, slug)).then((c) => { if (live) setGot(c); }).catch(() => { if (live) setGot('error'); });
    return () => { live = false; };
  }, [campaignId, segment, slug]);
  useLayoutEffect(() => {
    if (!anchor) return;
    const h = self.current?.getBoundingClientRect().height ?? 0;
    setAt(cardPlace(anchor.getBoundingClientRect(), h, window.innerWidth || 1024, window.innerHeight || 768));
  }, [anchor, got]);
  const card = got !== 'loading' && got !== 'error' && got !== null ? got : null;
  const shownName = card?.name ?? name;
  const shownFamily = card?.family ?? family;
  return (
    <div ref={self} data-testid="entry-card" aria-label={`${shownName}: rules card`} role="group"
      className="vtt-popover fixed z-50 p-2.5 text-xs shadow-lg"
      style={{ width: CARD_WIDTH, left: at?.left ?? -9999, top: at?.top ?? 0 }}>
      <div className="flex items-baseline gap-2">
        <strong className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-vtt-gold" data-testid="entry-card-name">{shownName}</strong>
        {shownFamily && <span className="shrink-0 rounded-full border border-vtt-line px-1.5 text-[0.625rem] leading-4 text-vtt-muted" data-testid="entry-card-family">{shownFamily}</span>}
      </div>
      {got === 'loading' && <p className="mt-1 text-stone-400" role="status">Reading the rules…</p>}
      {got === 'error' && <p className="mt-1 text-amber-300">The site can't show this entry just now.</p>}
      {got === null && <p className="mt-1 text-stone-400">The site has no card for this entry.</p>}
      {card?.summary && <p className="mt-1 text-stone-200" data-testid="entry-card-summary">{card.summary}</p>}
      {card && card.rows.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 font-sans" data-testid="entry-card-rows">
          {card.rows.map((r, i) => (
            <div key={`${r.label}-${i}`} className="contents">
              <dt className="text-vtt-muted">{r.label}</dt>
              <dd className="min-w-0 break-words text-stone-100">{r.text}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button type="button" className={btn} data-testid="entry-card-open" onClick={onOpen}>{openLabel}</button>
        {onStar && <button type="button" className={btn} data-testid="entry-card-star" aria-pressed={starred} disabled={starring} onClick={onStar}>{starred ? '★ Unstar' : '☆ Star'}</button>}
        {onPin && (pinned
          ? <><span className="self-center text-vtt-gold" data-testid="entry-card-pinned">★ Pinned</span><button type="button" className={btn} data-testid="entry-card-unpin" disabled={pinning} onClick={onPin}>Unpin</button></>
          : <button type="button" className={btn} data-testid="entry-card-pin" disabled={pinning} onClick={onPin}>Pin to DM screen</button>)}
        {onPlace && <button type="button" className={btn} data-testid="entry-card-place" onClick={onPlace}>Place</button>}
        {onGive && <button type="button" className={btn} data-testid="entry-card-give" onClick={onGive}>Give to…</button>}
        {onClose && <button type="button" className={`${btn} ml-auto`} data-testid="entry-card-close" onClick={onClose}>Close</button>}
      </div>
    </div>
  );
}
